import React, { useEffect, useContext } from 'react';
import { IconButton, Tooltip, FormControlLabel, Switch } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import config from '../../../config';
import DownloadIcon from '../../../theme/download';
import ReturnToMetricsLink from '../../common/ReturnToMetricsLink';
import html2canvas from 'html2canvas';
import Track from '../../../helpers/Analytics';
import { StateContext } from '../../../context/ChartContext';

const useStyles = makeStyles(theme => ({
  chartMenu: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '1px solid #ebebeb',
    margin: '10px -5px 0px -5px',
    padding: '10px 20px'
  },
  download: {
    paddingTop: 5,
    paddingRight: 15,
    cursor: 'pointer'
  },
  iconButton: {
    padding: 0,
    '&:hover': {
      background: 'transparent'
    },
    borderRadius: 0
  },
  downloadLinkText: {
    fontFamily: theme.typography.fontFamily,
    color: '#e91019',
    marginRight: 10,
    display: 'inline-block',
    float: 'left',
    fontSize: '13px',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}));

const ChartMenu = props => {
  const classes = useStyles();
  const { logarithmic, setLogarithmic, stacked, setStacked, fillStacked, setFillStacked, animationComplete, metricIds, metricNames } = props;
  const { chart } = useContext(StateContext);

  const chartElem = document.querySelector('#ChartImage');

  useEffect(() => {
    if (typeof window !== 'undefined' && chartElem) {
      const branding = document.createElement('div');
      branding.setAttribute('id', 'ChartBrandingLogo');
      branding.style.paddingLeft = `${chart.scales['x-axis-0'].right - 140}px`;
      branding.innerHTML = `<svg width="140" height="30.8" viewBox="0 0 770 170" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_2836_1573)">
      <path d="M283 103V114H248V57H282V68H260V80H280V91H260V103H283Z" fill="#111111"/>
      <path d="M356.92 114L351.3 76.8L334.31 114H331.27L314.27 76.81L308.64 114H296.72L305.49 57H317.63L332.77 91.33L347.99 57H360.16L368.84 114H356.92Z" fill="#111111"/>
      <path d="M419.2 103H396.11L392.13 114H379.32L401.66 57H413.3L436.36 114H423.25L419.2 103ZM400.16 92H415.12L407.6 71.62L400.16 92Z" fill="#111111"/>
      <path d="M469.73 95H462V114H450V57H472.39C483.5 57 491.78 64.86 491.78 75.85C491.78 83.31 487.81 89.4 481.67 92.44L493.94 113.99H480.6L469.73 94.99V95ZM462 83H472.03C477.27 83 480.4 79.85 480.4 75.5C480.4 71.15 477.27 68 472.03 68H462V83Z" fill="#111111"/>
      <path d="M541.6 114L522 86.14V114H510V57H522V83.37L540.2 57H554.17L535.17 84.43L556.23 114H541.6Z" fill="#111111"/>
      <path d="M604 103V114H569V57H603V68H581V80H601V91H581V103H604Z" fill="#111111"/>
      <path d="M660 68H644V114H632V68H616V57H660V68Z" fill="#111111"/>
      <path d="M709 103V114H674V57H709V68H687V80H707V91H687V103H709Z" fill="#111111"/>
      <path d="M745.73 95H738V114H726V57H748.39C759.5 57 767.78 64.86 767.78 75.85C767.78 83.31 763.81 89.4 757.67 92.44L769.94 113.99H756.6L745.73 94.99V95ZM738 83H748.03C753.27 83 756.4 79.85 756.4 75.5C756.4 71.15 753.27 68 748.03 68H738V83Z" fill="#111111"/>
      <path d="M78 114H0V135H78V114Z" fill="#E91019"/>
      <path d="M64 76H0V97H64V76Z" fill="#E91019"/>
      <path d="M174.24 126.09C171.47 106.46 162.25 27.75 162.25 27.75L119.17 90.35L90.75 36H0V57H76.45L115.63 131.58L147.44 84.66L151.89 135H175.31C175.31 135 174.49 127.86 174.24 126.09Z" fill="#E91019"/>
      <path d="M211 0V170Z" fill="#111111"/>
      <path d="M211 0V170" stroke="black" stroke-miterlimit="10"/>
      </g>
      <defs>
      <clipPath id="clip0_2836_1573">
      <rect width="770" height="170" fill="white"/>
      </clipPath>
      </defs>
      </svg>
      `;
      chartElem.appendChild(branding);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartElem]);

  var observer = new MutationObserver(function(mutations) {
    if (document.getElementById('ChartBrandingLogo')) {
      document.getElementById('ChartBrandingLogo').style.paddingRight = `${chart.width - chart.scales['x-axis-0'].right}px`;
    }
  });

  const chartMonitor = document.getElementById('ChartImage') ? document.getElementById('ChartImage').querySelector('canvas') : null;

  useEffect(() => {
    if (chartMonitor) {
      observer.observe(chartMonitor, { attributes: true, childList: true, subtree: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMonitor]);

  return (
    <div className={classes.chartMenu}>
      {config.enableLogarithmic ? (
        <FormControlLabel control={<Switch size='small' checked={logarithmic} onChange={() => setLogarithmic(!logarithmic)} />} label='Logarithmic' />
      ) : null}
      {config.enableStacked ? (
        <FormControlLabel control={<Switch size='small' checked={stacked} onChange={() => setStacked(!stacked)} />} label='Stacked' />
      ) : null}
      {stacked ? (
        <FormControlLabel control={<Switch size='small' checked={fillStacked} onChange={() => setFillStacked(!fillStacked)} />} label='Fill Lines' />
      ) : null}

      <ReturnToMetricsLink />

      <Tooltip title={animationComplete ? 'Export .png file' : 'Download unavailable while chart is animating. Please wait...'}>
        <div className={classes.download}>
          <IconButton
            onClick={() => {
              const chartImageElem = document.querySelector('#ChartImage');
              html2canvas(chartImageElem, {
                scrollX: window.scrollX,
                scrollY: -window.scrollY
              }).then(canvas => {
                // document.querySelector('#BrandingLogo').remove();
                const link = document.createElement('a');
                link.download = `EMARKETER_chart_${metricIds}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
              });
              Track('Metric', { id: 'Download-Chart', metrics: metricNames });
            }}
            className={classes.iconButton}
            disabled={!animationComplete}
          >
            <div className={classes.downloadLinkText}>Export Image</div>
            <DownloadIcon />
          </IconButton>
        </div>
      </Tooltip>
    </div>
  );
};

export default ChartMenu;
